import Cookies from '../providers/Cookies';
import { getPageContext, checkIfWidget } from '../utils';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';

const Amplitude = () => {
  const amplitudeKey = ENV !== 'prod' ? '91e90f83e2821646e578300893e14808' : '38836ac1de5b63f5664745ea2967f92f';
  const venuePathRegex = /\/(?:cities\/[\w-]+\/)?venues\/([\w-]+)(?:\/|$)/;
  const EVENT_NAMES_EXCLUDING_VENUE_INVENTORY = ['add to list', 'remove from list'];

  // Holds the actual venue name (from API venue data) for the current venue page,
  // set by the venue page component. Used to log the real venue name on `view page`
  // instead of the lossy URL-slug approximation.
  let currentVenueName = null;
  const setVenueName = (name) => {
    currentVenueName = name || null;
  };

  let currentVenueId = null;
  const setVenueId = (id) => {
    currentVenueId = id || null;
  };
  let lastWebContext = null;
  const resetTransientState = () => {
    lastWebContext = null;
  };

  const getContext = () => {
    const { pathname, hash } = window.location;
    const context = (checkIfWidget())
      ? getPageContext(hash.replaceAll('#', '')?.split('?')?.[0])
      : getPageContext(pathname);
    return context;
  };

  const getVenueNameFromPath = () => {
    const { pathname, hash } = window.location;
    const path = checkIfWidget()
      ? hash.replaceAll('#', '')?.split('?')?.[0]
      : pathname;
    const slugMatch = venuePathRegex.exec(path || '');

    if (!slugMatch?.[1]) {
      return null;
    }

    return decodeURIComponent(slugMatch[1]).replace(/-/g, ' ');
  };

  const getReferrer = () => {
    if (checkIfWidget()) {
      const referrer = window.__REFERRER__; // eslint-disable-line no-underscore-dangle
      if (referrer) {
        return getPageContext(referrer);
      }
      return 'partner website';
    }

    if (lastWebContext) {
      return lastWebContext;
    }

    const documentReferrer = document.referrer;
    if (documentReferrer) {
      try {
        const { pathname } = new URL(documentReferrer, window.location.origin);
        return getPageContext(pathname) || 'external';
      } catch (e) {
        return getPageContext(documentReferrer) || 'external';
      }
    }

    return 'direct';
  };

  const consumeHomepageCollection = (context) => {
    if (context !== 'venue page') {
      return undefined;
    }

    const currentSearch = window.location.search || '';
    const currentPath = window.location.pathname || '';
    const currentHash = window.location.hash || '';
    const searchParams = new URLSearchParams(currentSearch);
    const collection = searchParams.get('collection');
    if (!collection) {
      return undefined;
    }

    searchParams.delete('collection');
    const nextSearch = searchParams.toString();
    const nextUrl = `${currentPath}${nextSearch ? `?${nextSearch}` : ''}${currentHash}`;
    window.history.replaceState({}, '', nextUrl);
    return collection;
  };

  let currentVenueInventory = null;

  const setVenueInventory = (snapshot) => {
    currentVenueInventory = snapshot || null;
  };

  const doesViewPageHasNoContext = (event) => event.event_type === 'view page' && !getContext();

  const createAmplitudeInstance = () => {
    const params = new URLSearchParams(window.location.search);
    const extLinkValue = params.get('extlink');

    const pageViewTrackingEnrichment = () => ({
      name: 'page-view-tracking-enrichment',
      type: 'enrichment',
      setup: async () => undefined,
      execute: async (event) => {
        if (event.event_type !== '[Amplitude] Page Viewed') {
          return event;
        }
        const currentContext = getContext();
        const referrer = getReferrer();
        const homepageCollection = consumeHomepageCollection(currentContext);
        event.event_properties = {
          context: currentContext,
          referrer,
          venue_name: currentVenueName || getVenueNameFromPath(),
          venue_id: currentVenueId,
          url: window.location.href,
          'is widget': checkIfWidget(),
          ...(homepageCollection && { collection: homepageCollection }),
          ...(currentVenueInventory || {}),
          ...(extLinkValue && { extlink: extLinkValue }),
        };
        event.event_type = 'view page';
        if (doesViewPageHasNoContext(event)) {
          return null;
        }
        if (!checkIfWidget() && currentContext) {
          lastWebContext = currentContext;
        }
        return event;
      },
    });

    window.amplitude.add(pageViewTrackingEnrichment());
    window.amplitude.init(amplitudeKey);
  };

  if (window.amplitude) {
    createAmplitudeInstance();
  } else {
    window.addEventListener('ResyAmplitudeLoaded', () => {
      createAmplitudeInstance();
    });
  }

  const event = (name, props = {}) => {
    if (!amplitudeKey || !window.amplitude) return;
    const shouldIncludeVenueInventory = !EVENT_NAMES_EXCLUDING_VENUE_INVENTORY.includes(name);
    const shouldIncludeReferrer = ['submit rating', 'dismiss rating'].includes(name);

    const eventProps = {
      context: getContext(),
      'is widget': checkIfWidget(),
      ...(shouldIncludeReferrer ? { referrer: getReferrer() } : {}),
      ...(shouldIncludeVenueInventory ? (currentVenueInventory || {}) : {}),
      ...props,
    };

    window.amplitude?.track(name, eventProps);
  };

  const setUser = (userData) => {
    if (!amplitudeKey || !window.amplitude) return;
    window.amplitude.setUserId(userData.id);
    const identifyEvent = new window.amplitude.Identify();
    const paymentMethods = userData.payment_methods?.map((paymentMethod) => paymentMethod.type);
    Object.entries({
      'amex card product type': userData.amex_card_types,
      'card type': paymentMethods,
      'is amex': userData.amexCard,
      'is concierge': !!userData.is_concierge,
      'is gda': userData.is_global_dining_access,
      'user id': userData.id,
    })
      .forEach(([key, value]) => identifyEvent.set(key, value));
    window.amplitude.identify(identifyEvent);
  };

  const purchase = (token, data = {}) => {
    if (!amplitudeKey || !window.amplitude) return;
    const eventLabel = (data.resyToken) ? 'change booking' : 'complete booking';
    window.amplitude?.track(eventLabel, {
      context: getContext(),
      'is widget': checkIfWidget(),
      module: data.reservation?.module,
      venue: data.venueName,
      city: data.details?.venue?.location?.locality,
      'is event': !!data.reservation?.isEvent,
      event: data.reservation?.isEvent ? data.reservation?.title : undefined,
      'lead time': moment(data.reservation?.time).diff(moment().startOf('day'), 'days'),
      ...(data.reservation?.time && { time: moment(data.reservation.time).format(DATE_TIME_FORMAT) }),
      'party size': Math.floor(data.partySize) || 2,
      'add-ons': !!(data.items?.length > 0),
      'slot is GDA': !!data.reservation?.isGda,
      'venue is GDA': !!data.reservation?.venueIsGda,
      'reserve with google': (Cookies.get('_rwg')) ? true : undefined,
      'pre-paid': data.reservation?.payment?.is_paid,
      'cc required': !!(data.reservation?.payment?.cancellation_fee !== null
        || data.reservation?.payment?.is_paid
        || data.reservation?.allow_bypass_payment_method !== 1),
      searchSessionId: data.searchSessionId,
    });
  };

  return {
    event,
    setUser,
    purchase,
    setVenueName,
    setVenueInventory,
    setVenueId,
    resetTransientState,
  };
};

export default Amplitude();
